import type { RouteNamedMap } from "@/router/types";
import { type ItemNode, ItemNodeItemTypeEnum } from "@/services/internalApiV1";
import type { TableCellButtonConfig } from "@/types/table";
import type { LocationQueryRaw } from "vue-router";

// TODO: remove Tools:: prefix hack once we have migrated all item types to the new namespace
export const itemNodeTypeToRouteMapping: Partial<
  Record<ItemNodeItemTypeEnum | string, keyof RouteNamedMap>
> = {
  [ItemNodeItemTypeEnum.WORD_CLOUDS_WORD_CLOUD]: "word_clouds-show",
  [ItemNodeItemTypeEnum.PINBOARD_BOARD]: "pinboard-show",
  [ItemNodeItemTypeEnum.AI_ASSISTANT]: "ai_assistant-show",
  [ItemNodeItemTypeEnum.AUDIO_RECORDER_FILE]: "audio_recorder-show",
  ["Tools::AudioRecorder::File"]: "audio_recorder-show",
  [ItemNodeItemTypeEnum.CLASSROOM_SCREEN_SCREEN]: "classroom_screen-show",
  [ItemNodeItemTypeEnum.FILE_SHARING_FILE]: "file_sharing-show",
  [ItemNodeItemTypeEnum.SCREEN_RECORDER_FILE]: "screen_recorder-show",
  ["Tools::ScreenRecorder::File"]: "screen_recorder-show",
  [ItemNodeItemTypeEnum.SURVEYS_SURVEY]: "surveys-show",
  [ItemNodeItemTypeEnum.TEXT_EDITOR_DOCUMENT]: "text_editor-show",
  [ItemNodeItemTypeEnum.VIDEO_RECORDER_FILE]: "video_recorder-show",
  ["Tools::VideoRecorder::File"]: "video_recorder-show",
  [ItemNodeItemTypeEnum.WEBSITE_PAGE]: "website-show",
  [ItemNodeItemTypeEnum.WORKSHEET_WORKSHEET]: "worksheet-show",
  [ItemNodeItemTypeEnum.AI_FEEDBACK]: "feedback-show",
  [ItemNodeItemTypeEnum.PUPIL_FEEDBACK_ASSIGNMENT]: "pupil_feedback-show",
  [ItemNodeItemTypeEnum.LINK_SHORTENER_LINK]: "link_shortener-show",
  ["Tools::LinkShortener::Link"]: "link_shortener-show",
  [ItemNodeItemTypeEnum.AI_IMAGE]: "ai_image-show",
} as const;

/**
 * A route name whose params are exactly `{ id: string }`. The two-way `extends`
 * is what makes it exact: a route taking `{ id, pageId }` satisfies the first
 * check but not the second, and a route taking no params fails the first.
 */
type IdOnlyRoute<K extends keyof RouteNamedMap> =
  RouteNamedMap[K]["params"] extends { id: string }
    ? { id: string } extends RouteNamedMap[K]["params"]
      ? K
      : never
    : never;

/**
 * A share route a plain item id is enough to reach — derived from the route
 * table rather than listed, so the constraint is the compiler's rather than a
 * comment's. `classroom_screen-share` drops out on its own: it is
 * `/teaching_board/screens/:id/pages/:pageId/share`, and a push carrying only
 * `id` throws. A new `*-share` route taking just `:id` joins automatically.
 */
export type SingleIdShareRoute = {
  [K in keyof RouteNamedMap]: K extends `${string}-share`
    ? IdOnlyRoute<K>
    : never;
}[keyof RouteNamedMap];

/**
 * The share page each item type owns. Sharing has no row-level surface of its
 * own — it lives on the tool's `/<tool>/…/:id/share` page — so the Gemeinsame
 * Ablage's "Teilen" entry sends the user there.
 *
 * Listed explicitly rather than derived from the mapping above by swapping
 * `-show` for `-share`, for the reason `SingleIdShareRoute` records. An item
 * type absent here has no share page the entry can reach, and the entry hides
 * itself for it: AI feedback and AI image have none at all, and the classroom
 * screen's needs a page id.
 *
 * AI assistant is out too, though no longer for want of an id. Its show route
 * `/ai/assistants/:id` is a redirect onto the editor `/ai/chats/assistants/:id`
 * with the same id (#13751), so `ai_chat-assistants-share` would take a plain
 * item id. Offering "Teilen" for assistants is a product call, left to the
 * issue that asks for it.
 */
export const itemNodeTypeToShareRouteMapping: Partial<
  Record<ItemNodeItemTypeEnum | string, SingleIdShareRoute>
> = {
  [ItemNodeItemTypeEnum.WORD_CLOUDS_WORD_CLOUD]: "word_clouds-share",
  [ItemNodeItemTypeEnum.PINBOARD_BOARD]: "pinboard-share",
  [ItemNodeItemTypeEnum.AUDIO_RECORDER_FILE]: "audio_recorder-share",
  ["Tools::AudioRecorder::File"]: "audio_recorder-share",
  [ItemNodeItemTypeEnum.FILE_SHARING_FILE]: "file_sharing-share",
  [ItemNodeItemTypeEnum.SCREEN_RECORDER_FILE]: "screen_recorder-share",
  ["Tools::ScreenRecorder::File"]: "screen_recorder-share",
  [ItemNodeItemTypeEnum.SURVEYS_SURVEY]: "surveys-share",
  [ItemNodeItemTypeEnum.TEXT_EDITOR_DOCUMENT]: "text_editor-share",
  [ItemNodeItemTypeEnum.VIDEO_RECORDER_FILE]: "video_recorder-share",
  ["Tools::VideoRecorder::File"]: "video_recorder-share",
  [ItemNodeItemTypeEnum.WEBSITE_PAGE]: "website-share",
  [ItemNodeItemTypeEnum.WORKSHEET_WORKSHEET]: "worksheet-share",
  [ItemNodeItemTypeEnum.PUPIL_FEEDBACK_ASSIGNMENT]: "pupil_feedback-share",
  [ItemNodeItemTypeEnum.LINK_SHORTENER_LINK]: "link_shortener-share",
  ["Tools::LinkShortener::Link"]: "link_shortener-share",
} as const;

/**
 * The read-only public page each item type owns — the page a pupil reaches
 * through a share link. A library member who cannot edit a colleague's
 * material (`permissions.update` false, #13082) is sent here instead of the
 * editor. `getItemNodeGalleryQuery` asks the navigation guard to resolve the
 * actual gallery URL (including public_token) before mounting this page; the
 * public navbar provides Remix.
 *
 * Route names as in `useProjectItemTo.publicRouteName`, which did this for
 * shared projects (#10659) — minus the types whose public page is not a
 * preview but a *form that writes into the author's material*. Only
 * catalogable tools carry a `public_token`, and without one the page runs in
 * pupil mode: Schülerfeedback's page submits a response for a logged-in
 * teacher on mount, a word cloud with visitor write access shows the add-word
 * form, and the link shortener's page forwards off-site straight away. Those
 * types, and AI feedback (no public page at all), are absent, so the row gets
 * no link rather than the editor (review of #13123, finding 1). So is the AI
 * image: its only page is the owner's history, which 404s for a colleague.
 * The recorders and file sharing are not catalogable either, but their public
 * pages only play or download, so they stay.
 *
 * The AI assistant is absent for the same reason: its public page is the chat
 * itself, so a click dropped the teacher into a conversation with a
 * colleague's assistant instead of showing what it is. It opens the catalog's
 * assistant dialog in `ItemNodePreviewModal` instead (#13281). Pupil activity
 * lists and shared projects (`useProjectItemTo`) name the chat route
 * themselves and are not affected.
 */
export const itemNodeTypeToPublicRouteMapping: Partial<
  Record<ItemNodeItemTypeEnum | string, keyof RouteNamedMap>
> = {
  [ItemNodeItemTypeEnum.PINBOARD_BOARD]: "public-pinboard-show",
  [ItemNodeItemTypeEnum.AUDIO_RECORDER_FILE]: "audio_recorder-public-show",
  ["Tools::AudioRecorder::File"]: "audio_recorder-public-show",
  [ItemNodeItemTypeEnum.CLASSROOM_SCREEN_SCREEN]:
    "classroom_screen-public-show",
  [ItemNodeItemTypeEnum.FILE_SHARING_FILE]: "file_sharing-public-show",
  [ItemNodeItemTypeEnum.SCREEN_RECORDER_FILE]: "screen_recorder-public-show",
  ["Tools::ScreenRecorder::File"]: "screen_recorder-public-show",
  [ItemNodeItemTypeEnum.SURVEYS_SURVEY]: "survey-responses-public-new",
  [ItemNodeItemTypeEnum.TEXT_EDITOR_DOCUMENT]: "text_editor-public-show",
  [ItemNodeItemTypeEnum.VIDEO_RECORDER_FILE]: "video_recorder-public-show",
  ["Tools::VideoRecorder::File"]: "video_recorder-public-show",
  [ItemNodeItemTypeEnum.WEBSITE_PAGE]: "website-public-show",
  [ItemNodeItemTypeEnum.WORKSHEET_WORKSHEET]: "worksheet-responses-public-new",
} as const;

/**
 * `folderRouteName` lets a caller keep folder links inside its own Ablage scope
 * (see `useItemNodesScope`); tool items always open their tool, whatever the
 * scope.
 */
export function getItemNodeShowRoute(
  itemType?: ItemNodeItemTypeEnum | null,
  folderRouteName: keyof RouteNamedMap = "folders-show"
): keyof RouteNamedMap | undefined {
  const type = itemType ?? ItemNodeItemTypeEnum.FOLDER;

  if (type === ItemNodeItemTypeEnum.FOLDER) {
    return folderRouteName;
  }

  return itemNodeTypeToRouteMapping[type];
}

/**
 * The share page for a tool item, or `undefined` when the type has none — and
 * always `undefined` for a folder, which is not shareable.
 */
export function getItemNodeShareRoute(
  itemType?: ItemNodeItemTypeEnum | null
): SingleIdShareRoute | undefined {
  if (!itemType || itemType === ItemNodeItemTypeEnum.FOLDER) {
    return undefined;
  }

  return itemNodeTypeToShareRouteMapping[itemType];
}

/**
 * The public page for a tool item, or `undefined` when the type has none — and
 * always `undefined` for a folder, which has no page outside the Ablage.
 */
export function getItemNodePublicRoute(
  itemType?: ItemNodeItemTypeEnum | null
): keyof RouteNamedMap | undefined {
  if (!itemType || itemType === ItemNodeItemTypeEnum.FOLDER) {
    return undefined;
  }

  return itemNodeTypeToPublicRouteMapping[itemType];
}

/**
 * Where a table row opens: a folder in its Ablage, a tool item the user may
 * edit in its editor, and one they may not — a colleague's material in the
 * Gemeinsame Ablage — on its public page (#13082). The row's own
 * `permissions.update` decides, not authorship: it is `ItemNodePolicy#update?`
 * serialised with the row, the same answer the API gives the editor's save, so
 * it follows the backend when the rights model moves (#12387). An owner always
 * has `update`, which leaves Meine Ablage untouched.
 */
export function getItemNodeRowRoute(
  row: Pick<ItemNode, "itemType" | "permissions">,
  folderRouteName: keyof RouteNamedMap = "folders-show"
): keyof RouteNamedMap | undefined {
  const type = row.itemType ?? ItemNodeItemTypeEnum.FOLDER;

  if (type === ItemNodeItemTypeEnum.FOLDER || row.permissions.update) {
    return getItemNodeShowRoute(type, folderRouteName);
  }

  return getItemNodePublicRoute(type);
}

/**
 * A row that opens no page at all: a colleague's item whose type has no public
 * page (#13283). It opens the preview dialog instead of rendering as text.
 */
export function isItemNodePreviewOnly(
  row: Pick<ItemNode, "itemType" | "permissions">
): boolean {
  if (!row.itemType || row.itemType === ItemNodeItemTypeEnum.FOLDER) {
    return false;
  }

  return getItemNodeRowRoute(row) === undefined;
}

/**
 * The cell link of a preview-only row: a button that opens the preview dialog
 * (#13283). `null` where the row is not preview-only or no caller handles it.
 */
export function getItemNodePreviewCellLink<
  T extends Pick<ItemNode, "itemType" | "permissions">,
>(
  row: T,
  onPreview: ((row: T) => void) | undefined,
  ariaLabel: string
): TableCellButtonConfig | null {
  if (!onPreview || !isItemNodePreviewOnly(row)) return null;

  return { onClick: () => onPreview(row), ariaLabel };
}

/**
 * The query an author's link out of an Ablage carries — a row opening the
 * editor, or a freshly created item (#13336). The editor's breadcrumb reads it
 * back through `useSafeBackUrl` and leads to this folder instead of "Tools",
 * the same contract a colleague's preview link uses (`getItemNodeGalleryQuery`).
 */
export function getItemNodeFolderBackQuery(
  backTitle: string
): LocationQueryRaw {
  return { back_url: window.location.href, back_title: backTitle };
}

/** Resolve a library preview through the existing item API before mounting it.
 * Keeping a real route link also supports opening in a new tab.
 */
export function getItemNodeGalleryQuery(
  row: Pick<ItemNode, "itemType" | "permissions">,
  backTitle?: string
): LocationQueryRaw | undefined {
  if (row.permissions.update || !getItemNodePublicRoute(row.itemType)) {
    return undefined;
  }

  return {
    gallery_item_type: row.itemType,
    back_url: window.location.href,
    ...(backTitle ? { back_title: backTitle } : {}),
  };
}
